import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { X, Tag, Plus, ChevronLeft, ChevronRight, Loader2, CheckCircle2, RefreshCw, Save, Info, Sparkles, Wand2, Check } from 'lucide-react';
import { Button } from './Button';
import { cn } from '../../lib/utils';
import { createWorker } from 'tesseract.js';
import { motion, AnimatePresence } from 'framer-motion';

export interface TextItem {
  text: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

interface CropModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageUrl: string;
  pageTextItems?: TextItem[];
  onSave: (crop: { imageUrl: string, label: string }) => Promise<void>;
  currentPageNum: number;
  onNextPage?: () => void;
  onPrevPage?: () => void;
  hasNext?: boolean;
  hasPrev?: boolean;
  isLoadingPage?: boolean;
}

interface SelectionRect {
  x: number; 
  y: number; 
  w: number; 
  h: number; 
}

type InteractionMode = 'none' | 'moving' | 'resizing' | 'panning' | 'zooming';
type HandleType = 'tl' | 'tr' | 'bl' | 'br';

export const CropModal: React.FC<CropModalProps> = ({ 
  isOpen, 
  onClose, 
  imageUrl, 
  pageTextItems = [],
  onSave,
  currentPageNum,
  onNextPage,
  onPrevPage,
  hasNext,
  hasPrev,
  isLoadingPage
}) => {
  const [normSelection, setNormSelection] = useState<SelectionRect>({ x: 0.25, y: 0.25, w: 0.5, h: 0.3 });
  const [label, setLabel] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavedFeedback, setIsSavedFeedback] = useState(false);
  const [isAutoGenerated, setIsAutoGenerated] = useState(false);
  const [suggestionSource, setSuggestionSource] = useState<'ocr' | 'context' | null>(null);
  const [isLandscape, setIsLandscape] = useState(false);
  
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [imgDisplay, setImgDisplay] = useState({ w: 0, h: 0 });
  
  const imgRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const state = useRef({
    mode: 'none' as InteractionMode,
    handle: null as HandleType | null,
    lastPinchDist: 0,
    startPos: { x: 0, y: 0 },
    initialOffset: { x: 0, y: 0 },
    initialScale: 1,
    initialNorm: { x: 0, y: 0, w: 0, h: 0 } as SelectionRect
  });

  const syncLayout = useCallback(() => {
    if (!imgRef.current) return;
    const rect = imgRef.current.getBoundingClientRect();
    setImgDisplay({ w: rect.width / scale, h: rect.height / scale });
    setIsLandscape(window.innerHeight < 550 || (window.innerWidth / window.innerHeight > 1.2));
  }, [scale]);

  const resetView = () => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleResize = () => syncLayout();
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    const timer = setInterval(syncLayout, 1000);
    setTimeout(syncLayout, 100);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      clearInterval(timer);
    };
  }, [isOpen, syncLayout]);

  useEffect(() => {
    if (isOpen) {
      setLabel('');
      setIsAutoGenerated(false);
      setSuggestionSource(null);
      resetView();
      setNormSelection({ x: 0.3, y: 0.3, w: 0.4, h: 0.3 });
    }
  }, [isOpen, imageUrl]);

  const checkTextSuggestions = useCallback((norm: SelectionRect) => {
    if (!pageTextItems.length) return false;

    const foundItems = pageTextItems.filter(item => {
      const itemNormX = item.x / imgDisplay.w;
      const itemNormY = item.y / imgDisplay.h;
      
      const hIntersect = itemNormX >= norm.x - 0.05 && itemNormX <= norm.x + norm.w + 0.05;
      const vProximity = itemNormY >= norm.y && itemNormY <= norm.y + norm.h + 0.1;
      
      return hIntersect && vProximity && item.text.length > 2;
    });

    if (foundItems.length > 0) {
      const likelyLabels = foundItems.filter(i => i.text.length > 3 && !/^\d+$/.test(i.text.trim()));
      const bestMatch = likelyLabels.length > 0 ? likelyLabels[0] : foundItems[0];
      
      const cleanLabel = bestMatch.text.trim().replace(/[:;]$/, "");
      if (cleanLabel.length >= 2) {
        setLabel(cleanLabel.charAt(0).toUpperCase() + cleanLabel.slice(1).slice(0, 50));
        setIsAutoGenerated(true);
        setSuggestionSource('context');
        return true;
      }
    }
    return false;
  }, [pageTextItems, imgDisplay]);

  const performOCR = useCallback(async (norm: SelectionRect) => {
    if (!imgRef.current) return;
    
    const foundSuggestion = checkTextSuggestions(norm);
    if (foundSuggestion) return;

    setIsScanning(true);
    setSuggestionSource(null);
    try {
      const { naturalWidth: nw, naturalHeight: nh } = imgRef.current;
      const rx = norm.x * nw;
      const ry = (norm.y + norm.h) * nh; 
      const rw = norm.w * nw;
      const rh = Math.min(400, nh - ry);
      if (rh < 20) { setIsScanning(false); return; }

      const canvas = document.createElement('canvas');
      canvas.width = rw; canvas.height = rh;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.drawImage(imgRef.current, rx, ry, rw, rh, 0, 0, rw, rh);
      
      const worker = await createWorker('eng');
      const { data: { text } } = await worker.recognize(canvas.toDataURL('image/jpeg', 0.8));
      await worker.terminate();
      
      const words = text.replace(/[\r\n]+/g, ' ').split(/\s+/).filter(w => {
        const word = w.replace(/[^a-zA-Z0-9]/g, "").trim();
        if (word.length <= 1) return false;
        if (/^\d+[xX]$/.test(word)) return false;
        if (/^\d+$/.test(word)) return false;
        return true;
      });

      const finalLabel = words.join(' ').trim();
      if (finalLabel.length >= 2) {
        setLabel(finalLabel.charAt(0).toUpperCase() + finalLabel.slice(1).slice(0, 50));
        setIsAutoGenerated(true);
        setSuggestionSource('ocr');
      }
    } catch (e) { 
      console.warn("OCR failed", e); 
    } finally { 
      setIsScanning(false); 
    }
  }, [checkTextSuggestions]);

  const startAction = (mode: InteractionMode, e: React.TouchEvent | React.MouseEvent, handle?: HandleType) => {
    e.stopPropagation();
    const touch = 'touches' in e.nativeEvent ? e.nativeEvent.touches[0] : (e.nativeEvent as MouseEvent);
    const m = { x: touch.clientX, y: touch.clientY, dist: 0, count: 'touches' in e.nativeEvent ? e.nativeEvent.touches.length : 1 };
    
    if ('touches' in e.nativeEvent && e.nativeEvent.touches.length >= 2) {
      m.dist = Math.hypot(e.nativeEvent.touches[0].clientX - e.nativeEvent.touches[1].clientX, e.nativeEvent.touches[0].clientY - e.nativeEvent.touches[1].clientY);
    }

    let nextMode = m.count >= 2 ? 'zooming' : mode;
    let currentNorm = { ...normSelection };

    if (mode === 'panning' && m.count === 1 && imgRef.current) {
        const rect = imgRef.current.getBoundingClientRect();
        const relX = (m.x - rect.left) / scale;
        const relY = (m.y - rect.top) / scale;
        const nX = relX / imgDisplay.w;
        const nY = relY / imgDisplay.h;
        if (nX >= 0 && nX <= 1 && nY >= 0 && nY <= 1) {
          currentNorm = {
            ...normSelection,
            x: Math.max(0, Math.min(1 - normSelection.w, nX - normSelection.w / 2)),
            y: Math.max(0, Math.min(1 - normSelection.h, nY - normSelection.h / 2))
          };
          setNormSelection(currentNorm);
          nextMode = 'moving';
        }
    }

    state.current = {
      ...state.current,
      mode: nextMode as InteractionMode,
      handle: handle || null,
      startPos: { x: m.x, y: m.y },
      lastPinchDist: m.dist,
      initialOffset: { ...offset },
      initialScale: scale,
      initialNorm: currentNorm
    };
  };

  const handleGlobalMove = useCallback((e: MouseEvent | TouchEvent) => {
    if (state.current.mode === 'none') return;
    const touch = 'touches' in e ? e.touches[0] : (e as MouseEvent);
    const m = { x: touch.clientX, y: touch.clientY, dist: 0, count: 'touches' in e ? e.touches.length : 1 };
    
    if ('touches' in e && e.touches.length >= 2) {
      m.dist = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY);
      if (state.current.lastPinchDist > 0 && m.dist > 0) {
        setScale(Math.max(1, Math.min(10, state.current.initialScale * (m.dist / state.current.lastPinchDist))));
      }
    }

    const dx = m.x - state.current.startPos.x;
    const dy = m.y - state.current.startPos.y;

    if (state.current.mode === 'panning' || m.count >= 2) {
      setOffset({ x: state.current.initialOffset.x + dx, y: state.current.initialOffset.y + dy });
    } else if (state.current.mode === 'moving') {
      setNormSelection(prev => ({
        ...prev,
        x: Math.max(0, Math.min(1 - prev.w, state.current.initialNorm.x + (dx / scale) / imgDisplay.w)),
        y: Math.max(0, Math.min(1 - prev.h, state.current.initialNorm.y + (dy / scale) / imgDisplay.h))
      }));
    } else if (state.current.mode === 'resizing' && state.current.handle) {
      const { initialNorm, handle } = state.current;
      const nDx = (dx / scale) / imgDisplay.w;
      const nDy = (dy / scale) / imgDisplay.h;
      const minSize = 0.05; let next = { ...initialNorm };

      if (handle.includes('t')) { 
        const nextY = Math.max(0, Math.min(initialNorm.y + initialNorm.h - minSize, initialNorm.y + nDy));
        next.h = initialNorm.h + (initialNorm.y - nextY);
        next.y = nextY;
      } else if (handle.includes('b')) {
        next.h = Math.max(minSize, Math.min(1 - initialNorm.y, initialNorm.h + nDy));
      }
      if (handle.includes('l')) {
        const nextX = Math.max(0, Math.min(initialNorm.x + initialNorm.w - minSize, initialNorm.x + nDx));
        next.w = initialNorm.w + (initialNorm.x - nextX);
        next.x = nextX;
      } else if (handle.includes('r')) {
        next.w = Math.max(minSize, Math.min(1 - initialNorm.x, initialNorm.w + nDx));
      }
      setNormSelection(next);
    }
  }, [scale, imgDisplay]);

  const stopAction = useCallback(() => {
    if (state.current.mode === 'resizing' || state.current.mode === 'moving') {
      performOCR(normSelection);
    }
    state.current.mode = 'none';
  }, [normSelection, performOCR]);

  useEffect(() => {
    if (!isOpen) return;
    window.addEventListener('mousemove', handleGlobalMove);
    window.addEventListener('mouseup', stopAction);
    window.addEventListener('touchmove', handleGlobalMove, { passive: false });
    window.addEventListener('touchend', stopAction);
    return () => {
      window.removeEventListener('mousemove', handleGlobalMove);
      window.removeEventListener('mouseup', stopAction);
      window.removeEventListener('touchmove', handleGlobalMove);
      window.removeEventListener('touchend', stopAction);
    };
  }, [isOpen, handleGlobalMove, stopAction]);

  const handleSaveCrop = async () => {
    if (!imgRef.current || !label.trim() || isSaving) return;
    setIsSaving(true);
    try {
      const canvas = document.createElement('canvas');
      const { naturalWidth: nw, naturalHeight: nh } = imgRef.current;
      canvas.width = normSelection.w * nw;
      canvas.height = normSelection.h * nh;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.drawImage(imgRef.current, normSelection.x * nw, normSelection.y * nh, normSelection.w * nw, normSelection.h * nh, 0, 0, canvas.width, canvas.height);
      const imageUrl = canvas.toDataURL('image/jpeg', 0.95);
      await onSave({ imageUrl, label });
      setIsSavedFeedback(true); setLabel(''); setIsAutoGenerated(false); setSuggestionSource(null);
      setTimeout(() => setIsSavedFeedback(false), 2000);
    } catch (e) {
      console.error("Failed to save crop:", e);
    } finally {
      setIsSaving(false);
    }
  };

  const pixelSelection = useMemo(() => ({
    x: normSelection.x * imgDisplay.w,
    y: normSelection.y * imgDisplay.h,
    w: normSelection.w * imgDisplay.w,
    h: normSelection.h * imgDisplay.h,
  }), [normSelection, imgDisplay]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-slate-950 flex flex-col items-stretch no-select touch-none overflow-hidden font-inter h-[100dvh]">
      <div 
        ref={containerRef}
        className="flex-1 relative flex items-center justify-center overflow-hidden cursor-crosshair"
        onMouseDown={(e) => startAction('panning', e)}
        onTouchStart={(e) => startAction('panning', e)}
      >
        <motion.div className="relative inline-flex items-center justify-center will-change-transform" style={{ x: offset.x, y: offset.y, scale }} transition={{ type: 'spring', damping: 30, stiffness: 250 }}>
          {isLoadingPage ? (
            <div className="flex flex-col items-center gap-6 text-white/30">
              <Loader2 className="h-10 w-10 animate-spin text-primary-500" />
              <span className="text-[9px] font-black tracking-[0.4em] uppercase">Loading Page Source</span>
            </div>
          ) : (
            <div className="relative">
              <img ref={imgRef} onLoad={syncLayout} src={imageUrl} alt="Context" className="max-w-[100vw] max-h-[100vh] object-contain block pointer-events-none" draggable={false} />
              <div className="absolute pointer-events-none" style={{ width: imgDisplay.w, height: imgDisplay.h, left: 0, top: 0 }}>
                <div className="absolute inset-0 overflow-hidden">
                  <div className="absolute bg-slate-950/70 backdrop-blur-[1px]" style={{ left: 0, top: 0, right: 0, height: pixelSelection.y }} />
                  <div className="absolute bg-slate-950/70 backdrop-blur-[1px]" style={{ left: 0, top: pixelSelection.y + pixelSelection.h, right: 0, bottom: 0 }} />
                  <div className="absolute bg-slate-950/70 backdrop-blur-[1px]" style={{ left: 0, top: pixelSelection.y, width: pixelSelection.x, height: pixelSelection.h }} />
                  <div className="absolute bg-slate-950/70 backdrop-blur-[1px]" style={{ left: pixelSelection.x + pixelSelection.w, top: pixelSelection.y, right: 0, height: pixelSelection.h }} />
                </div>
                <div 
                  className={cn("absolute border border-primary-500/50 ring-2 ring-primary-500/20 pointer-events-auto cursor-move", state.current.mode === 'moving' ? "bg-primary-500/5" : "")} 
                  style={{ left: pixelSelection.x, top: pixelSelection.y, width: pixelSelection.w, height: pixelSelection.h }}
                  onMouseDown={(e) => startAction('moving', e)}
                  onTouchStart={(e) => startAction('moving', e)}
                >
                  <div className="absolute -top-6 -left-1 px-2 py-0.5 bg-primary-500 text-[8px] text-white font-black uppercase tracking-widest rounded-sm pointer-events-none shadow-lg">Target Area</div>
                  {['tl', 'tr', 'bl', 'br'].map((h) => (
                    <div key={h} className={cn("absolute w-10 h-10 flex items-center justify-center pointer-events-auto", h.includes('t') ? "-top-5" : "-bottom-5", h.includes('l') ? "-left-5" : "-right-5")} onMouseDown={(e) => startAction('resizing', e, h as HandleType)} onTouchStart={(e) => startAction('resizing', e, h as HandleType)}>
                      <div className={cn("w-4 h-4 border-primary-500 shadow-xl", h === 'tl' && "border-t-[3px] border-l-[3px] rounded-tl-[1px]", h === 'tr' && "border-t-[3px] border-r-[3px] rounded-tr-[1px]", h === 'bl' && "border-b-[3px] border-l-[3px] rounded-bl-[1px]", h === 'br' && "border-b-[3px] border-r-[3px] rounded-tr-[1px]")} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </div>

      <div className={cn("absolute top-0 left-0 right-0 z-50 flex justify-between items-center bg-gradient-to-b from-slate-950/90 to-transparent pointer-events-none pt-safe", isLandscape ? "p-3" : "p-4")}>
        <div className="flex gap-2 pointer-events-auto">
          <div className={cn("bg-slate-900/95 backdrop-blur-2xl rounded-2xl border border-white/10 flex items-center shadow-2xl transition-all overflow-hidden", isLandscape ? "h-12" : "h-16")}>
            <button onClick={onPrevPage} disabled={!hasPrev || isLoadingPage} className="text-white/50 hover:text-white disabled:opacity-10 px-3"><ChevronLeft className={cn(isLandscape ? "h-5 w-5" : "h-8 w-8")} /></button>
            <div className={cn("bg-primary-600/20 border-x border-white/10 flex items-center justify-center px-6 min-w-[5rem]", isLandscape ? "h-12" : "h-16")}>
              <span className={cn("font-black text-white uppercase tracking-tight", isLandscape ? "text-xl" : "text-3xl")}>{currentPageNum}</span>
            </div>
            <button onClick={onNextPage} disabled={!hasNext || isLoadingPage} className="text-white/50 hover:text-white disabled:opacity-10 px-3"><ChevronRight className={cn(isLandscape ? "h-5 w-5" : "h-8 w-8")} /></button>
          </div>
        </div>
        <div className="flex items-center gap-2 pointer-events-auto">
          <button onClick={onClose} className={cn("bg-slate-900/95 backdrop-blur-2xl text-white/50 hover:text-white rounded-2xl border border-white/10 shadow-2xl flex items-center justify-center", isLandscape ? "w-12 h-12" : "w-16 h-16")}><X className={cn(isLandscape ? "h-6 w-6" : "h-7 w-7")} /></button>
        </div>
      </div>

      <div className={cn("absolute left-0 right-0 z-[60] flex flex-col items-center justify-center px-4 pointer-events-none transition-all duration-300 pb-safe", isLandscape ? "bottom-4" : "bottom-6 sm:bottom-12")}>
        <AnimatePresence>
          {isAutoGenerated && !isScanning && (
            <motion.div initial={{ opacity: 0, y: 10, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 10 }} className="backdrop-blur-md px-5 py-2 rounded-full border shadow-2xl flex items-center gap-3 mb-3 z-0 pointer-events-auto transition-colors bg-amber-500 border-amber-400">
              <span className="text-[10px] font-black text-white uppercase tracking-[0.2em] whitespace-nowrap">Review for Accuracy</span>
            </motion.div>
          )}
        </AnimatePresence>

        <div className={cn("w-full bg-[#1c2331]/95 backdrop-blur-3xl border border-white/10 flex shadow-[0_40px_120px_rgba(0,0,0,0.95)] pointer-events-auto transition-all relative z-10", isLandscape ? "max-w-3xl flex-row items-center gap-3 p-2 rounded-full h-18" : "max-w-xl flex-col gap-6 p-6 rounded-[2.5rem]")}>
          <div className={cn("flex-1 min-w-0 flex items-center", isLandscape ? "h-full" : "w-full")}>
            <div className="relative w-full h-full flex items-center">
              <Tag className="absolute left-5 h-5 w-5 text-primary-500 z-20" />
              <input 
                type="text" 
                value={label} 
                onChange={(e) => { setLabel(e.target.value); setIsAutoGenerated(false); setSuggestionSource(null); }} 
                placeholder={isScanning ? "Identifying..." : "Name of Tissue..."} 
                className={cn("w-full bg-[#121826]/80 border border-white/5 focus:bg-[#182030] focus:border-primary-500/40 outline-none transition-all placeholder:text-slate-600 uppercase tracking-[0.1em]", isLandscape ? "py-3 pl-14 pr-14 text-sm rounded-full h-14" : "py-5 pl-14 pr-14 text-base rounded-2xl", isAutoGenerated ? "text-amber-400 font-black" : "text-white font-black")} 
              />
              <div className="absolute right-5 flex items-center gap-2 z-20">
                {isScanning ? <Loader2 className="h-4 w-4 animate-spin text-primary-500" /> : <button onClick={() => performOCR(normSelection)} className="p-2 text-slate-500 hover:text-primary-400"><RefreshCw className="h-4 w-4" /></button>}
              </div>
            </div>
          </div>
          
          <Button onClick={handleSaveCrop} disabled={!label.trim() || isScanning || isSaving || isSavedFeedback} className={cn("transition-all font-black uppercase tracking-[0.2em] flex-shrink-0 shadow-2xl", isLandscape ? "h-14 px-12 rounded-full text-xs w-auto" : "w-full h-18 rounded-[1.5rem] text-sm", isSavedFeedback ? "bg-emerald-600" : "bg-primary-600 hover:bg-primary-500")}>
            {isSaving ? <Loader2 className="h-6 w-6 animate-spin" /> : isSavedFeedback ? <Check className="h-5 w-5 mr-2" /> : <Save className="h-5 w-5 mr-3" />}
            {isSavedFeedback ? "Captured" : "ADD TO LIBRARY"}
          </Button>
        </div>
      </div>
    </div>
  );
}